import React from "react";
import {
  Card, Row, Col, Tag, Typography, Space, Statistic,
  Descriptions, Alert, Divider, Tooltip
} from "antd";
import {
  CodeOutlined, GlobalOutlined, MailOutlined, CloudServerOutlined,
  UserOutlined, ApiFilled, WarningOutlined, CalendarOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";

const { Title, Text, Paragraph } = Typography;

// Helper para Tags con estado + Tooltip
const DisabledTag = ({ enabled, color, icon, label }) => {
  const tooltipText = "Servicio no disponible. Consulte con el proveedor";

  return (
    <Tooltip title={!enabled ? tooltipText : null}>
      <Tag
        icon={icon}
        color={enabled ? color : "default"}
        style={{ cursor: enabled ? "default" : "not-allowed" }}
      >
        {label}
      </Tag>
    </Tooltip>
  );
};

const LicenceCard = ({
  licence,
  product,
  payment,
  status,
  statusProps,
  expiryText,
  nextPaymentText,
  daysUntilExpiry,
}) => (
  <Card bordered={false} className="licences-card">
    {/* Cabecera */}
    <Row justify="space-between" align="top" wrap={false} style={{ marginBottom: "16px" }}>
      <Col flex="auto">
        <Title level={3} style={{ marginTop: 0 }}>
          {product.name || "N/A"}
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: "8px" }}>
          Forma de pago: <Text strong>{payment.name || "N/A"}</Text>. {payment.description}
        </Paragraph>
      </Col>
      <Col flex="none">
        <Tag icon={statusProps.icon} color={statusProps.color} className="licences-tag">
          <span style={{ color: "black" }}>
            {status.name || "N/A"}
          </span>
        </Tag>
      </Col>
    </Row>

    {/* Tags */}
    <Space wrap style={{ marginBottom: "24px" }}>
      <DisabledTag
        enabled={product.api_active}
        color="green"
        icon={<CodeOutlined />}
        label="API REST"
      />

      <DisabledTag
        enabled={product.custom_domain}
        color="blue"
        icon={<GlobalOutlined />}
        label="Dominio Propio"
      />

      <DisabledTag
        enabled={product.custom_email}
        color="purple"
        icon={<MailOutlined />}
        label="Email Personalizado"
      />

      <DisabledTag
        enabled={product.white_label}
        color="gold"
        icon={<CloudServerOutlined />}
        label="Marca Blanca"
      />
    </Space>

    <Paragraph style={{ minHeight: "40px" }}>
      {product.description || "Descripción del producto no disponible."}
    </Paragraph>

    <Divider />

    {/* Estadísticas */}
    <Row gutter={16}>
      <Col span={12}>
        <Card bordered={false} className="licences-stats-card">
          <Statistic
            title="Usuarios Disponibles"
            value={`${product.number_of_users - licence.available_users}/${product.number_of_users}`}
            prefix={<UserOutlined />}
            valueStyle={{ color: "#046A38" }}
          />
        </Card>
      </Col>
      <Col span={12}>
        <Card bordered={false} className="licences-stats-card">
          <Statistic
            title="Activos Disponibles"
            value={`${product.number_of_assets - licence.available_assets}/${product.number_of_assets}`}
            prefix={<ApiFilled />}
            valueStyle={{ color: "#046A38" }}
          />
        </Card>
      </Col>
    </Row>

    <Divider />

    {/* Fechas */}
    <Title level={5}>Información del Contrato</Title>
    <Descriptions bordered column={2} size="small">
      <Descriptions.Item className="description-item" label="Activo desde">
        <Text strong>
          {licence.active_since_on ? dayjs(licence.active_since_on).format("DD/MM/YYYY") : "N/A"}
        </Text>
      </Descriptions.Item>
      <Descriptions.Item label="Fin de contrato">
        <Text strong>
          {licence.defeated_date ? dayjs(licence.defeated_date).format("DD/MM/YYYY") : "N/A"}
        </Text>
      </Descriptions.Item>
      <Descriptions.Item label="Último pago">
        <Text strong>
          {licence.last_payment_date ? dayjs(licence.last_payment_date).format("DD/MM/YYYY") : "N/A"}
        </Text>
      </Descriptions.Item>
      <Descriptions.Item label="Próximo pago">
        <Text strong>
          {licence.next_payment_date ? dayjs(licence.next_payment_date).format("DD/MM/YYYY") : "N/A"}
        </Text>
      </Descriptions.Item>
    </Descriptions>

    {/* Alertas */}
    <Space direction="vertical" style={{ width: "100%", marginTop: "24px" }}>
      {expiryText && (
        <Alert
          message={expiryText}
          type={daysUntilExpiry === 0 ? "error" : daysUntilExpiry < 30 ? "error" : "warning"}
          showIcon
          icon={<WarningOutlined />}
          className="licences-alert"
        />
      )}
      {nextPaymentText && (
        <Alert message={nextPaymentText} type="success" showIcon icon={<CalendarOutlined />} className="licences-alert" />
      )}
    </Space>

    <Text className="licences-duration">
      Duración del contrato: {licence.duration_months}{" "}
      {licence.duration_months === 1 ? "mes" : "meses"}
    </Text>
  </Card>
);

export default LicenceCard;